import React from "react";

interface LoadingSpinnerProps {
  /**
   * Layout variant.
   *   - "block" (default): large centered spinner in a min-h 200px box.
   *     Use inside a table body, a card, or a route-level loader.
   *   - "inline": small spinner sized to fit next to button text.
   *     Use for button-embedded "loading..." indicators.
   */
  variant?: "block" | "inline";
  /** Optional accessible label read by screen readers. */
  label?: string;
}

/**
 * Two layout variants keep the component single-import across the app.
 * Existing call sites that render ``<LoadingSpinner />`` keep their block
 * behavior unchanged; new call sites that need a button-inline spinner
 * opt in with ``variant="inline"``.
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  variant = "block",
  label = "Loading",
}) => {
  if (variant === "inline") {
    return (
      <span
        role="status"
        aria-label={label}
        className="inline-block w-3 h-3 align-middle border-2 border-current border-solid rounded-full animate-spin border-t-transparent"
      />
    );
  }

  return (
    <div
      className="flex items-center justify-center p-8 min-h-[200px]"
      role="status"
      aria-label={label}
    >
      <div className="relative w-12 h-12">
        <div className="absolute inset-0 border-4 border-gray-200 border-solid rounded-full" />
        <div className="absolute inset-0 border-4 border-blue-500 border-solid rounded-full animate-spin border-t-transparent" />
      </div>
    </div>
  );
};

export default LoadingSpinner;
